import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { tournamentsApi } from '../../api/tournaments';
import { cardsApi } from '../../api/cards';
import { authApi } from '../../api/auth';
import { Search, Play, Save, ArrowLeft } from 'lucide-react';
import FootballField, { tournamentScoresDisplayAsDefinitive } from './FootballField';
import { pickNextMatchForTournamentWindow } from '../../utils/pickNextMatchForTournamentWindow';
import {
  collectFormationTeamCardIds,
  filterFormationCardsWithoutNa,
  filterWorldCupConvocatedCards,
  formatUpcomingMatchFirstLabel,
  isMissingUpcomingMatchFirst,
  isWorldCupTournament
} from '../../utils/matchCallUpUtils';
import usePlayerStatsUpdates from '../../hooks/usePlayerStatsUpdates';
import '../../styles/formation-gaming.css';

function getCardPlayerId(card) {
  if (!card) return null;
  const raw = card.player?.id ?? card.player_id ?? card.player_id_from_players;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** Carte già “in formazione” per il controllo duplicato giocatore nel modale (stessa linea = pending quando attivo). */
function getFormationCardsForPlayerDupCheck(team, pendingLineSelection) {
  const out = [];
  const push = (c) => {
    if (c) out.push(c);
  };
  if (pendingLineSelection?.role === 'DEF') {
    (pendingLineSelection.selected || []).forEach(push);
    push(team.goalkeeper);
    (team.midfielders || []).forEach(push);
    (team.attackers || []).forEach(push);
    push(team.reserves?.RES1);
    push(team.reserves?.RES2);
    push(team.reserves?.RES3);
    push(team.legend);
  } else if (pendingLineSelection?.role === 'MID') {
    (pendingLineSelection.selected || []).forEach(push);
    push(team.goalkeeper);
    (team.defenders || []).forEach(push);
    (team.attackers || []).forEach(push);
    push(team.reserves?.RES1);
    push(team.reserves?.RES2);
    push(team.reserves?.RES3);
    push(team.legend);
  } else if (pendingLineSelection?.role === 'ATT') {
    (pendingLineSelection.selected || []).forEach(push);
    push(team.goalkeeper);
    (team.defenders || []).forEach(push);
    (team.midfielders || []).forEach(push);
    push(team.reserves?.RES1);
    push(team.reserves?.RES2);
    push(team.reserves?.RES3);
    push(team.legend);
  } else {
    push(team.goalkeeper);
    (team.defenders || []).forEach(push);
    (team.midfielders || []).forEach(push);
    (team.attackers || []).forEach(push);
    push(team.reserves?.RES1);
    push(team.reserves?.RES2);
    push(team.reserves?.RES3);
    push(team.legend);
  }
  return out;
}

const TournamentFormation = ({ viewOnly = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user] = useState(authApi.getCurrentUser());
  const [tournament, setTournament] = useState(null);
  const [rateLimitError, setRateLimitError] = useState(null);
  const [userCards, setUserCards] = useState([]);
  const [usedCardsInOtherTournaments, setUsedCardsInOtherTournaments] = useState([]);
  const [formation, setFormation] = useState('4-3-3');
  const [team, setTeam] = useState({
    goalkeeper: null,
    defenders: [],
    midfielders: [],
    attackers: [],
    reserves: { RES1: null, RES2: null, RES3: null },
    legend: null
  });
  const [filters, setFilters] = useState({
    search: '',
    rarity: '',
    position: '',
    nationality: '',
    team: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  // Multi-select per linea (DEF/MID/ATT): selezione multipla fino al numero indicato dal modulo
  const [pendingLineSelection, setPendingLineSelection] = useState(null);
  // Cache card per ruolo: evita di rifare getUserCards + N getNextMatch ad ogni click su slot
  const userCardsCacheRef = useRef({});
  const [cardScores, setCardScores] = useState({});   // Map<cardId, score>
  const [cardDetails, setCardDetails] = useState({}); // Map<cardId, {matchDate, goals, ...}>
  const [cardLiveNoData, setCardLiveNoData] = useState({}); // Map<cardId, boolean> - partita live ma SportMonks senza dati
  const [cardNotPlayed, setCardNotPlayed] = useState({}); // Map<cardId, boolean> - partita giocata ma giocatore non sceso (NP)
  const [cardMatchDateTime, setCardMatchDateTime] = useState({}); // Map<cardId, ISO string> - data/ora partita per card senza score
  const [cardMatchInfo, setCardMatchInfo] = useState({}); // Map<cardId, {homeTeam, awayTeam, competitionName}> - partita per card senza score
  const [matchByPlayerId, setMatchByPlayerId] = useState({}); // Map<playerId, {matchDateTime, homeTeam, awayTeam, competitionName}> - fallback per card in altra tabella
  const [gameweekMatches, setGameweekMatches] = useState([]); // Partite della gameweek per il pannello
  const [cardXp, setCardXp] = useState({}); // Map<cardId, { xp_percentage, xp_level }>
  const [scoresLoaded, setScoresLoaded] = useState(false); // Indica se i voti sono stati caricati almeno una volta
  const toBoolFlag = useCallback((value) => {
    if (value === true || value === 1 || value === '1') return true;
    if (typeof value === 'string') {
      const v = value.trim().toLowerCase();
      return v === 'true' || v === 'yes' || v === 'on';
    }
    return false;
  }, []);
  const fetchNextMatchForCards = useCallback(async (cards, currentTournament) => {
    if (!cards?.length) return [];
    if (!currentTournament || !currentTournament.start_date || !currentTournament.end_date) {
      return cards.map((card) => ({
        ...card,
        next_match: null,
        match_id: null,
        playsInRange: null,
        upcoming_match_first: null,
        upcoming_match_second: null
      }));
    }

    try {
      const templateIdByCardId = {};
      const rarityByCardId = {};
      const playerIdByCardId = {};
      cards.forEach((c) => {
        if (c?.id != null && c?.template_id != null) {
          templateIdByCardId[c.id] = c.template_id;
        }
        if (c?.id != null && c?.rarity?.name) {
          rarityByCardId[c.id] = c.rarity.name;
        }
        const playerId = c?.player?.id ?? c?.player_id ?? c?.player_id_from_players;
        if (c?.id != null && playerId != null && !Number.isNaN(Number(playerId))) {
          playerIdByCardId[c.id] = Number(playerId);
        }
      });
      const batchRes = await cardsApi.getNextMatchesBatch(
        cards.map((c) => c.id),
        Object.keys(templateIdByCardId).length ? templateIdByCardId : undefined,
        Object.keys(rarityByCardId).length ? rarityByCardId : undefined,
        Object.keys(playerIdByCardId).length ? playerIdByCardId : undefined
      );
      const matches = batchRes?.success ? batchRes.matches || {} : {};
      return cards.map((card) => {
        const m = matches[card.id];
        const picked = pickNextMatchForTournamentWindow(m, currentTournament.start_date, currentTournament.end_date);
        return {
          ...card,
          next_match: picked.next_match,
          match_id: picked.match_id,
          playsInRange: picked.playsInRange,
          upcoming_match_first: m?.next_match ?? null,
          upcoming_match_second: m?.next_match_2 ?? null
        };
      });
    } catch (error) {
      return cards.map((card) => ({
        ...card,
        next_match: null,
        match_id: null,
        playsInRange: null,
        upcoming_match_first: null,
        upcoming_match_second: null
      }));
    }
  }, []);

  // Formazioni valide compatibili con gli slot persistiti in team_cards:
  // max 5 DEF, max 5 MID, max 3 ATT.
  const allFormations = [
    '4-3-3', '4-4-2', '4-5-1', 
    '3-4-3', '3-5-2', 
    '5-3-2', '5-4-1',
    '5-2-3'
  ];
  
  const validFormations = allFormations.filter(form => {
    const parts = form.split('-').map(Number);
    let defenders = 0, midfielders = 0, attackers = 0;
    
    if (parts.length === 3) {
      defenders = parts[0];
      midfielders = parts[1];
      attackers = parts[2];
    } else if (parts.length === 4) {
      defenders = parts[0];
      midfielders = parts[1] + parts[2];
      attackers = parts[3];
    } else if (parts.length === 5) {
      defenders = parts[0];
      midfielders = parts[1] + parts[2];
      attackers = parts[3] + parts[4];
    }

    if (defenders > 5 || midfielders > 5 || attackers > 3) {
      return false;
    }
    
    return defenders >= 3 && midfielders >= 3 && attackers >= 1;
  });

  const getFormationCounts = (form) => {
    const parts = (form || formation).split('-').map(Number);
    let maxDef = 4, maxMid = 3, maxAtt = 3;
    if (parts.length === 3) {
      maxDef = parts[0]; maxMid = parts[1]; maxAtt = parts[2];
    } else if (parts.length === 4) {
      maxDef = parts[0]; maxMid = parts[1] + parts[2]; maxAtt = parts[3];
    } else if (parts.length === 5) {
      maxDef = parts[0]; maxMid = parts[1] + parts[2]; maxAtt = parts[3] + parts[4];
    }
    return { maxDef, maxMid, maxAtt };
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadTournament();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  // Rimossa l'aggiornamento automatico ogni 30 secondi - ora i voti vengono caricati solo su richiesta

  const loadTournament = async () => {
    try {
      setLoading(true);
      const response = await tournamentsApi.getById(parseInt(id));
      console.log('[loadTournament] Full API response:', response);
      if (response?.success) {
        console.log('[loadTournament] Tournament data:', response.tournament);
        console.log('[loadTournament] Tournament gameweek_id:', response.tournament?.gameweek_id);
        setTournament(response.tournament);
        
        // Carica le card già usate in altri tornei del gameweek PRIMA di caricare la formazione
        let usedCards = [];
        if (response.tournament?.type === 'gameweek' && user?.id) {
          usedCards = await loadUsedCardsInGameweek(response.tournament);
        }
        
        // Carica la formazione esistente (dopo aver caricato le carte usate)
        const loadedTeam = await loadExistingFormation(response.tournament, usedCards);
        
        // Carica voti per tornei in corso, upcoming, pending_payment o closed (scores finali)
        const status = response.tournament?.gameweek_status || response.tournament?.status;
        const shouldLoadScores = ['in_progress', 'upcoming', 'pending_payment', 'closed', 'close'].includes(status);
        if (shouldLoadScores && user?.id) {
          await loadCardScores(response.tournament.id, user.id, true, loadedTeam);
        }
      }
    } catch (error) {
      console.error('Error loading tournament:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExistingFormation = async (tournamentData, usedCards = []) => {
    let loadedTeam = null;
    try {
      console.log('[loadExistingFormation] Loading formation for tournament:', id, 'user:', user.id);
      const response = await tournamentsApi.getFormation(parseInt(id), user.id);
      console.log('[loadExistingFormation] Full API response:', JSON.stringify(response, null, 2));
      
      if (response?.success && response.team) {
        console.log('[loadExistingFormation] Team data received:', response.team);
        console.log('[loadExistingFormation] Team object:', response.team.team);
        console.log('[loadExistingFormation] Formation:', response.team.formation);
        
        // Ensure team structure is correct
        const teamData = response.team.team || response.team;
        if (teamData) {
          // Normalize team structure to ensure all fields exist
          const normalizedTeam = {
            goalkeeper: teamData.goalkeeper || null,
            defenders: Array.isArray(teamData.defenders) ? teamData.defenders : [],
            midfielders: Array.isArray(teamData.midfielders) ? teamData.midfielders : [],
            attackers: Array.isArray(teamData.attackers) ? teamData.attackers : [],
            reserves: {
              RES1: teamData.reserves?.RES1 || null,
              RES2: teamData.reserves?.RES2 || null,
              RES3: teamData.reserves?.RES3 || null
            },
            legend: teamData.legend || null
          };
          
          console.log('[loadExistingFormation] Normalized team:', normalizedTeam);
          console.log('[loadExistingFormation] Setting team state...');
          loadedTeam = normalizedTeam;
          setTeam(normalizedTeam);
          
          if (response.team.formation) {
            console.log('[loadExistingFormation] Setting formation:', response.team.formation);
            setFormation(response.team.formation);
          }
          
          // Check if loaded formation contains cards already used in other tournaments
          const allLoadedCardIds = [
            normalizedTeam.goalkeeper?.id,
            ...(normalizedTeam.defenders || []).map(c => c?.id).filter(id => id != null),
            ...(normalizedTeam.midfielders || []).map(c => c?.id).filter(id => id != null),
            ...(normalizedTeam.attackers || []).map(c => c?.id).filter(id => id != null),
            normalizedTeam.reserves?.RES1?.id,
            normalizedTeam.reserves?.RES2?.id,
            normalizedTeam.reserves?.RES3?.id
          ].filter(id => id != null);
          
          console.log('[loadExistingFormation] Loaded card IDs:', allLoadedCardIds);
          
          const conflictingCards = allLoadedCardIds.filter(cardId => 
            usedCards.includes(cardId)
          );
          
          if (conflictingCards.length > 0) {
            console.warn(`[loadExistingFormation] Loaded formation contains ${conflictingCards.length} card(s) already used in other tournaments. User will need to replace them.`);
            // Don't block loading, but the save will be blocked if they try to save without fixing
          }
        } else {
          console.warn('[loadExistingFormation] No team data found in response, keeping empty team');
        }
      } else {
        console.log('[loadExistingFormation] No existing formation found (this is normal for first-time entry)');
        // Keep the empty team state - this is expected for first-time entry
      }
    } catch (error) {
      console.error('[loadExistingFormation] Error loading existing formation:', error);
      // Don't reset team on error - keep current state
    }
    return loadedTeam;
  };

  const loadCardScores = async (tournamentId, userId, skipRateLimit = false, currentTeam = null) => {
    try {
      console.log(`[loadCardScores] Chiamato — tournamentId=${tournamentId} userId=${userId} skipRateLimit=${skipRateLimit}`);
      setRateLimitError(null);
      
      // Controllo rate limiting lato client usando localStorage (skip se è un aggiornamento real-time)
      if (!skipRateLimit) {
        const lastCheckKey = `last_score_check_${tournamentId}_${userId}`;
        const lastCheckTime = localStorage.getItem(lastCheckKey);
        
        if (lastCheckTime) {
          const lastCheck = new Date(lastCheckTime);
          const now = new Date();
          const minutesSinceLastCheck = (now.getTime() - lastCheck.getTime()) / (1000 * 60);
          
          if (minutesSinceLastCheck < 1) {
            const secondsRemaining = Math.ceil(60 - ((now.getTime() - lastCheck.getTime()) / 1000));
            setRateLimitError(`Hai già controllato i voti di recente. Riprova tra ${secondsRemaining} secondo${secondsRemaining !== 1 ? 'i' : ''}.`);
            setScoresLoaded(true);
            return; // Non fare la chiamata se non è passato 1 minuto
          }
        }
      }
      
      const response = await tournamentsApi.getFormationScores(tournamentId, userId);
      console.log('[loadCardScores] Risposta API:', JSON.stringify({ 
        success: response?.success, 
        cardScoresCount: response?.cardScores ? Object.keys(response.cardScores).length : 0,
        cardDetailsCount: response?.cardDetails ? Object.keys(response.cardDetails).length : 0,
        cardDetailsKeys: response?.cardDetails ? Object.keys(response.cardDetails) : []
      }));
      if (response?.success) {
        // Salva il timestamp dell'ultima chiamata riuscita (solo se non è un aggiornamento real-time)
        if (!skipRateLimit) {
          const lastCheckKey = `last_score_check_${tournamentId}_${userId}`;
          localStorage.setItem(lastCheckKey, new Date().toISOString());
        }
        
        setScoresLoaded(true);
        if (response.cardScores) {
          setCardScores(response.cardScores);
        }
        if (response.cardDetails) {
          setCardDetails(response.cardDetails);
        }
        setCardLiveNoData(response.cardLiveNoData || {});
        setCardNotPlayed(response.cardNotPlayed || {});
        setCardMatchDateTime(response.cardMatchDateTime || {});
        setCardMatchInfo(response.cardMatchInfo || {});
        setMatchByPlayerId(response.matchByPlayerId || {});
        setGameweekMatches(response.gameweekMatches || []);
        setCardXp(response.cardXp || {});
      }
    } catch (error) {
      console.error('Error loading card scores:', error);
      if (error.response?.status === 429 && !skipRateLimit) {
        // Rate limit exceeded dal backend (solo se non è un aggiornamento real-time)
        const errorMessage = error.response.data?.message || 'Hai già controllato i voti di recente. Riprova tra 1 minuto.';
        setRateLimitError(errorMessage);
        
        // Salva il timestamp anche in caso di errore 429 per evitare chiamate future
        const lastCheckKey = `last_score_check_${tournamentId}_${userId}`;
        localStorage.setItem(lastCheckKey, new Date().toISOString());
        
        setScoresLoaded(true);
      } else {
        setScoresLoaded(true); // Marca come caricato anche in caso di errore
      }
    }
  };

  const loadUsedCardsInGameweek = async (currentTournament) => {
    try {
      console.log('[loadUsedCardsInGameweek] Tournament data:', currentTournament);
      console.log('[loadUsedCardsInGameweek] gameweek_id:', currentTournament?.gameweek_id);
      
      if (currentTournament?.type !== 'gameweek' || !user?.id) {
        console.log('[loadUsedCardsInGameweek] Skipping - not a gameweek tournament or no user');
        return [];
      }
      
      if (!currentTournament?.gameweek_id) {
        console.error('[loadUsedCardsInGameweek] ERROR: Tournament is gameweek type but gameweek_id is missing!', currentTournament);
        return [];
      }

      // Use gameweek_id as the unique identifier
      const gameweekId = currentTournament.gameweek_id;
      console.log(`[loadUsedCardsInGameweek] Loading used cards for gameweek ID: ${gameweekId}, tournament ID: ${currentTournament.id}`);
      
      // Use the API endpoint that queries directly by gameweek_id
      const response = await tournamentsApi.getUsedCardsInGameweek(user.id, gameweekId, currentTournament.id);
      
      if (response?.success && Array.isArray(response.usedCards)) {
        console.log(`[loadUsedCardsInGameweek] Loaded ${response.usedCards.length} used cards for gameweek ID ${gameweekId}:`, response.usedCards);
        setUsedCardsInOtherTournaments(response.usedCards);
        return response.usedCards;
      }
      
      console.warn('[loadUsedCardsInGameweek] No used cards returned or invalid response:', response);
      return [];
    } catch (error) {
      console.error('Error loading used cards in gameweek:', error);
      return [];
    }
  };

  const loadUserCards = useCallback(async (positionFilter = null) => {
    if (!user?.id) return null;
    setLoading(true);
    try {
      const filtersToUse = { ...filters };
      
      // CRITICAL: Filter by tournament rarity restriction
      if (tournament?.rarity_restriction && tournament.rarity_restriction !== 'all') {
        filtersToUse.rarity = tournament.rarity_restriction;
      }
      
      if (positionFilter) {
        if (positionFilter.role === 'GK') filtersToUse.position = 'GK';
        else if (positionFilter.role === 'DEF' || positionFilter.role === 'RES1') filtersToUse.position = 'DEF';
        else if (positionFilter.role === 'MID' || positionFilter.role === 'RES2') filtersToUse.position = 'MID';
        else if (positionFilter.role === 'ATT' || positionFilter.role === 'RES3') filtersToUse.position = 'ATT';
        else if (positionFilter.role === 'LEGEND') filtersToUse.rarity = 'legend';
        else if (positionFilter.role === 'DIVINITY') filtersToUse.rarity = 'divinity';
      }
      const response = await cardsApi.getUserCards(user.id, filtersToUse);
      if (!response?.success) return null;
      const loadedCards = response.cards || [];
      let result = loadedCards.length > 0 && tournament
        ? await fetchNextMatchForCards(loadedCards, tournament)
        : loadedCards;
      result = filterWorldCupConvocatedCards(result, tournament);
      setUserCards(result);
      return result;
    } catch (error) {
      console.error('Error loading cards:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [user?.id, filters, tournament?.rarity_restriction, tournament]);

  // Invalidare cache quando cambia torneo o rarità
  useEffect(() => {
    userCardsCacheRef.current = {};
  }, [tournament?.id, tournament?.rarity_restriction]);

  useEffect(() => {
    if (!modalOpen || !selectedPosition || !tournament?.id || !user?.id) return;
    const roleKey = selectedPosition.role === 'RES1' ? 'DEF' : selectedPosition.role === 'RES2' ? 'MID' : selectedPosition.role === 'RES3' ? 'ATT' : selectedPosition.role;
    const cacheKey = `${tournament.id}-${tournament.rarity_restriction || 'all'}-${roleKey}`;
    const cached = userCardsCacheRef.current[cacheKey];
    if (cached !== undefined) {
      setUserCards(Array.isArray(cached) ? cached : []);
      return;
    }
    const ensureUsedCardsLoaded = async () => {
      if (tournament?.type === 'gameweek' && user?.id) {
        await loadUsedCardsInGameweek(tournament);
      }
      const cards = await loadUserCards(selectedPosition);
      if (cards !== null && cards !== undefined) {
        userCardsCacheRef.current[cacheKey] = cards;
      }
    };
    ensureUsedCardsLoaded();
  }, [modalOpen, selectedPosition, loadUserCards, tournament, user?.id]);

  // Listen for player stats updates via SSE (only in viewOnly mode when tournament is in progress)
  const handleStatsUpdated = useCallback((data) => {
    // When stats are updated, reload card scores if we're in viewOnly mode
    if (viewOnly && tournament?.id && tournament?.status === 'in_progress' && user?.id) {
      console.log('Player stats updated, reloading card scores...', data);
      // Reload scores without rate limit check (this is a real-time update)
      loadCardScores(tournament.id, user.id, true); // Pass true to skip rate limit
    }
  }, [viewOnly, tournament?.id, tournament?.status, user?.id, loadCardScores]);

  usePlayerStatsUpdates(handleStatsUpdated, {
    enabled: viewOnly && tournament?.status === 'in_progress'
  });

  useEffect(() => {
    if (!modalOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [modalOpen]);

  const handlePositionClick = (role, index = null) => {
    console.log('[handlePositionClick] Opening modal for position:', role, 'tournament:', tournament?.name);

    if (tournament?.rarity_restriction && tournament.rarity_restriction !== 'all') {
      setFilters((prev) => ({ ...prev, rarity: tournament.rarity_restriction }));
    }

    setSelectedPosition({ role, index });
    const counts = getFormationCounts();
    if (role === 'DEF') {
      setPendingLineSelection({ role: 'DEF', selected: [...(team.defenders || [])], max: counts.maxDef });
    } else if (role === 'MID') {
      setPendingLineSelection({ role: 'MID', selected: [...(team.midfielders || [])], max: counts.maxMid });
    } else if (role === 'ATT') {
      setPendingLineSelection({ role: 'ATT', selected: [...(team.attackers || [])], max: counts.maxAtt });
    } else {
      setPendingLineSelection(null);
    }
    setModalOpen(true);

    // Load used cards in background — don't block modal open on slow mobile networks
    if (tournament?.type === 'gameweek' && user?.id) {
      void loadUsedCardsInGameweek(tournament);
    }
  };

  const handleCardSelectFromModal = (card) => {
    if (!selectedPosition) return;
    if (usedCardsInOtherTournaments.includes(card.id)) return;
    const isLegendSlot = selectedPosition.role === 'LEGEND' || selectedPosition.role === 'DIVINITY';
    if (!isLegendSlot && isMissingUpcomingMatchFirst(card)) return;

    if (pendingLineSelection) {
      const { role, selected, max } = pendingLineSelection;
      const idx = selected.findIndex(c => c.id === card.id);
      if (idx >= 0) {
        const next = selected.filter((_, i) => i !== idx);
        setPendingLineSelection({ ...pendingLineSelection, selected: next });
      } else if (selected.length < max) {
        setPendingLineSelection({ ...pendingLineSelection, selected: [...selected, card] });
      }
      return;
    }

    const { role } = selectedPosition;
    handleCardSelect(card, role);
    setModalOpen(false);
    setSelectedPosition(null);
  };

  const handleConfirmLineSelection = () => {
    if (!pendingLineSelection) return;
    const { role, selected } = pendingLineSelection;
    if (role === 'DEF') setTeam(prev => ({ ...prev, defenders: [...selected] }));
    else if (role === 'MID') setTeam(prev => ({ ...prev, midfielders: [...selected] }));
    else if (role === 'ATT') setTeam(prev => ({ ...prev, attackers: [...selected] }));
    setPendingLineSelection(null);
    setModalOpen(false);
    setSelectedPosition(null);
  };

  const handleCardSelect = (card, position) => {
    // CRITICAL CHECK: prevent selecting cards already used in other tournaments
    // Cards are already disabled in the UI, so just silently return if somehow clicked
    if (usedCardsInOtherTournaments.includes(card.id)) {
      console.warn(`Card ${card.id} is already used in another tournament of this gameweek - selection blocked`);
      return;
    }

    const isAlreadyInDefendersEarly = team.defenders.find(c => c.id === card.id);
    const isAlreadyInMidfieldersEarly = team.midfielders.find(c => c.id === card.id);
    const isAlreadyInAttackersEarly = team.attackers.find(c => c.id === card.id);
    const isAlreadyInReservesEarly =
      team.reserves.RES1?.id === card.id ||
      team.reserves.RES2?.id === card.id ||
      team.reserves.RES3?.id === card.id;
    const isAlreadyInGoalkeeperEarly = team.goalkeeper?.id === card.id;
    const isAlreadyInLegendEarly = team.legend?.id === card.id;
    const isAlreadyPlacedInTeam =
      isAlreadyInGoalkeeperEarly ||
      isAlreadyInDefendersEarly ||
      isAlreadyInMidfieldersEarly ||
      isAlreadyInAttackersEarly ||
      isAlreadyInReservesEarly ||
      isAlreadyInLegendEarly;

    if (
      position !== 'LEGEND' &&
      position !== 'DIVINITY' &&
      !isAlreadyPlacedInTeam &&
      isMissingUpcomingMatchFirst(card)
    ) {
      return;
    }
    
    const cardPosition = card.player?.position?.toUpperCase() || '';
    const isAlreadyInDefenders = isAlreadyInDefendersEarly;
    const isAlreadyInMidfielders = isAlreadyInMidfieldersEarly;
    const isAlreadyInAttackers = isAlreadyInAttackersEarly;
    const isAlreadyInReserves = isAlreadyInReservesEarly;
    const isAlreadyInGoalkeeper = isAlreadyInGoalkeeperEarly;
    const isAlreadyInLegend = isAlreadyInLegendEarly;
    
    // Check if card is already used anywhere in the team
    const isAlreadyInTeam = isAlreadyInGoalkeeper || isAlreadyInDefenders || isAlreadyInMidfielders || isAlreadyInAttackers || isAlreadyInReserves || isAlreadyInLegend;
    const cardKind = String(card.card_kind || card.cardKind || '').toLowerCase();
    const rarityName = String(card.rarity?.name || '').toLowerCase();
    const hasLegendFlag = !!card.player?.is_legend;
    const hasDivinityFlag = !!card.player?.is_divinity;
    const isLegendCard = cardKind === 'legend' || rarityName === 'legend' || hasLegendFlag;
    const isDivinityCard = cardKind === 'divinity' || rarityName === 'divinity' || hasDivinityFlag;

    if (position === 'LEGEND' || position === 'DIVINITY') {
      if (position === 'LEGEND' && !isLegendCard) {
        alert('Puoi selezionare solo carte Legend in questo slot.');
        return;
      }
      if (position === 'DIVINITY' && !isDivinityCard) {
        alert('Puoi selezionare solo carte Divinity in questo slot.');
        return;
      }
      if (isAlreadyInTeam && !isAlreadyInLegend) {
        alert('This player is already selected in another position. Please remove them first.');
        return;
      }
      setTeam({ ...team, legend: isAlreadyInLegend ? null : card });
      return;
    }
    
    const totalFieldPlayers = team.defenders.length + team.midfielders.length + team.attackers.length;
    const maxFieldPlayers = 10;
    const isFieldComplete = team.goalkeeper && totalFieldPlayers === 10;
    
    if (isFieldComplete && !isAlreadyInReserves && !isAlreadyInDefenders && !isAlreadyInMidfielders && !isAlreadyInAttackers) {
      if (cardPosition.startsWith('DEF') && !team.reserves.RES1) {
        setTeam({ ...team, reserves: { ...team.reserves, RES1: card } });
        return;
      } else if (cardPosition.startsWith('MID') && !team.reserves.RES2) {
        setTeam({ ...team, reserves: { ...team.reserves, RES2: card } });
        return;
      } else if (cardPosition.startsWith('ATT') && !team.reserves.RES3) {
        setTeam({ ...team, reserves: { ...team.reserves, RES3: card } });
        return;
      }
    }
    
    if (position === 'GK') {
      // If clicking on same card, remove it; if clicking on different card and current is already in team, prevent
      if (isAlreadyInTeam && !isAlreadyInGoalkeeper) {
        alert('This player is already selected in another position. Please remove them first.');
        return;
      }
      setTeam({ ...team, goalkeeper: team.goalkeeper?.id === card.id ? null : card });
    } else if (position === 'DEF') {
      if (isAlreadyInTeam && !isAlreadyInDefenders && !isAlreadyInReserves) {
        alert('This player is already selected in another position. Please remove them first.');
        return;
      }
      if (team.reserves.RES1?.id === card.id) {
        setTeam({ ...team, reserves: { ...team.reserves, RES1: null } });
      }
      const parts = formation.split('-').map(Number);
      const maxDef = parts.length >= 1 ? parts[0] : 4;
      const defenders = team.defenders.filter(c => c.id !== card.id);
      if (!isAlreadyInDefenders) {
        if (defenders.length < maxDef) {
          setTeam({ ...team, defenders: [...defenders, card] });
        } else {
          alert(`Formation ${formation} requires exactly ${maxDef} defenders. Current: ${team.defenders.length}`);
        }
      } else {
        setTeam({ ...team, defenders });
      }
    } else if (position === 'MID') {
      if (isAlreadyInTeam && !isAlreadyInMidfielders && !isAlreadyInReserves) {
        alert('This player is already selected in another position. Please remove them first.');
        return;
      }
      if (team.reserves.RES2?.id === card.id) {
        setTeam({ ...team, reserves: { ...team.reserves, RES2: null } });
      }
      const parts = formation.split('-').map(Number);
      const maxMid = parts.length === 3 ? parts[1] : parts.length === 4 ? parts[1] + parts[2] : parts.length === 5 ? parts[1] + parts[2] : 3;
      const midfielders = team.midfielders.filter(c => c.id !== card.id);
      if (!isAlreadyInMidfielders) {
        if (midfielders.length < maxMid) {
          setTeam({ ...team, midfielders: [...midfielders, card] });
        } else {
          alert(`Formation ${formation} requires exactly ${maxMid} midfielders. Current: ${team.midfielders.length}`);
        }
      } else {
        setTeam({ ...team, midfielders });
      }
    } else if (position === 'ATT') {
      if (isAlreadyInTeam && !isAlreadyInAttackers && !isAlreadyInReserves) {
        alert('This player is already selected in another position. Please remove them first.');
        return;
      }
      if (team.reserves.RES3?.id === card.id) {
        setTeam({ ...team, reserves: { ...team.reserves, RES3: null } });
      }
      const parts = formation.split('-').map(Number);
      const maxAtt = parts.length === 3 ? parts[2] : parts.length === 4 ? parts[3] : parts.length === 5 ? parts[3] + parts[4] : 3;
      const attackers = team.attackers.filter(c => c.id !== card.id);
      if (!isAlreadyInAttackers) {
        if (attackers.length < maxAtt) {
          setTeam({ ...team, attackers: [...attackers, card] });
        } else {
          alert(`Formation ${formation} requires exactly ${maxAtt} attackers. Current: ${team.attackers.length}`);
        }
      } else {
        setTeam({ ...team, attackers });
      }
    }
    
    if (isAlreadyInReserves) {
      if (team.reserves.RES1?.id === card.id) {
        setTeam({ ...team, reserves: { ...team.reserves, RES1: null } });
      } else if (team.reserves.RES2?.id === card.id) {
        setTeam({ ...team, reserves: { ...team.reserves, RES2: null } });
      } else if (team.reserves.RES3?.id === card.id) {
        setTeam({ ...team, reserves: { ...team.reserves, RES3: null } });
      }
    }
  };

  const handleSave = async () => {
    const parts = formation.split('-').map(Number);
    let expectedDef = 0, expectedMid = 0, expectedAtt = 0;
    
    if (parts.length === 3) {
      expectedDef = parts[0];
      expectedMid = parts[1];
      expectedAtt = parts[2];
    } else if (parts.length === 4) {
      expectedDef = parts[0];
      expectedMid = parts[1] + parts[2];
      expectedAtt = parts[3];
    } else if (parts.length === 5) {
      expectedDef = parts[0];
      expectedMid = parts[1] + parts[2];
      expectedAtt = parts[3] + parts[4];
    }
    
    if (!team.goalkeeper) {
      alert('Please select 1 goalkeeper');
      return;
    }
    
    if (team.defenders.length < 3) {
      alert('Please select at least 3 defenders');
      return;
    }
    
    if (team.midfielders.length < 3) {
      alert('Please select at least 3 midfielders');
      return;
    }
    
    if (team.attackers.length < 1) {
      alert('Please select at least 1 attacker');
      return;
    }
    
    if (team.defenders.length !== expectedDef || team.midfielders.length !== expectedMid || team.attackers.length !== expectedAtt) {
      alert(`Team composition must match formation ${formation}. Required: ${expectedDef} DEF, ${expectedMid} MID, ${expectedAtt} ATT. Current: ${team.defenders.length} DEF, ${team.midfielders.length} MID, ${team.attackers.length} ATT.`);
      return;
    }
    
    const totalFieldPlayers = team.defenders.length + team.midfielders.length + team.attackers.length;
    if (totalFieldPlayers !== 10) {
      alert(`You must have exactly 11 players on the field (1 GK + ${totalFieldPlayers} field players). Required: 1 GK + 10 field players.`);
      return;
    }
    
    if (!team.reserves.RES1 || !team.reserves.RES2 || !team.reserves.RES3) {
      alert('Please select all 3 reserves: RES1 (defender), RES2 (midfielder), RES3 (attacker)');
      return;
    }
    
    if (!team.reserves.RES1.player?.position?.toUpperCase().startsWith('DEF')) {
      alert('RES1 must be a defender');
      return;
    }
    if (!team.reserves.RES2.player?.position?.toUpperCase().startsWith('MID')) {
      alert('RES2 must be a midfielder');
      return;
    }
    if (!team.reserves.RES3.player?.position?.toUpperCase().startsWith('ATT')) {
      alert('RES3 must be an attacker');
      return;
    }

    setSaving(true);
    try {
      // Prepare team with match_id for each card
      const teamWithMatchIds = {
        ...team,
        goalkeeper: team.goalkeeper ? { ...team.goalkeeper, match_id: team.goalkeeper.match_id || null } : null,
        defenders: team.defenders.map(def => ({ ...def, match_id: def.match_id || null })),
        midfielders: team.midfielders.map(mid => ({ ...mid, match_id: mid.match_id || null })),
        attackers: team.attackers.map(att => ({ ...att, match_id: att.match_id || null })),
        reserves: {
          RES1: team.reserves.RES1 ? { ...team.reserves.RES1, match_id: team.reserves.RES1.match_id || null } : null,
          RES2: team.reserves.RES2 ? { ...team.reserves.RES2, match_id: team.reserves.RES2.match_id || null } : null,
          RES3: team.reserves.RES3 ? { ...team.reserves.RES3, match_id: team.reserves.RES3.match_id || null } : null
        }
      };
      
      const response = await tournamentsApi.updateFormation(parseInt(id), {
        userId: user.id,
        team: teamWithMatchIds,
        formation: formation
      });
      
      if (response?.success) {
        alert('Formation updated successfully!');
        navigate('/profile');
      }
    } catch (error) {
      console.error('Error saving formation:', error);
      alert('Failed to save formation: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !tournament) {
    return (
      <div className="min-h-screen football-background pt-20 flex items-center justify-center">
        <div className="football-spinner"></div>
      </div>
    );
  }

  return (
    <div className="formation-container">
      {/* Formation Background */}
      <div 
        className="formation-background"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%), url('/images/homebg.png')`
        }}
      ></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <button
          onClick={() => navigate('/profile')}
          className="btn-football mb-6 flex items-center space-x-2"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Profile</span>
        </button>

        <h1 className="text-4xl font-bold mb-8">{tournament?.name || 'Tournament Formation'}</h1>

        <div className="football-card glass-football p-6 mb-6">
          <h2 className="text-2xl font-bold mb-6">Edit Your Team Formation</h2>
          
          {!viewOnly && (
            <div className="mb-6">
              <label className="block font-semibold mb-2">Select Formation</label>
              <select
                value={formation}
                onChange={(e) => setFormation(e.target.value)}
                className="w-full md:w-auto min-w-[200px] p-3 rounded-lg border-2 border-[rgba(65,105,225,0.3)] bg-white text-[#1e293b] font-medium focus:border-[#4169E1] focus:outline-none focus:ring-2 focus:ring-[#4169E1]/50 transition-all appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%231e293b' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 1rem center',
                  paddingRight: '2.5rem'
                }}
              >
                {validFormations.map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
              <p className="text-sm mt-2">
                Selected: {formation} - Click on empty positions on the field to select players
              </p>
            </div>
          )}
          
          {viewOnly && formation && (
            <div className="mb-6">
              <div className="text-sm text-gray-300">
                <span className="font-semibold">Formation:</span> {formation}
              </div>
            </div>
          )}
          
          {tournament?.status === 'in_progress' && scoresLoaded && !viewOnly && (
            <div className="mt-3 p-4 voti-gameweek-panel">
              <div className="flex items-start space-x-2">
                <span className="text-[#00D9FF] font-bold text-lg">ℹ️</span>
                <div className="flex-1">
                  <div className="font-bold text-white text-sm mb-1">Voti Gameweek</div>
                  <div className="text-gray-300 text-sm leading-relaxed">
                    Clicca su "Check Scores" per visualizzare i voti delle tue card. Puoi controllare i voti una volta ogni ora.
                    {Object.keys(cardScores).length === 0 || Object.values(cardScores).every(score => score === null || score === undefined) 
                      ? <span className="font-semibold text-white"> I voti non sono ancora disponibili per questa gameweek.</span>
                      : (
                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block min-w-[28px] px-1.5 py-0.5 rounded-full bg-emerald-600 text-white text-[9px] font-bold flex items-center justify-center">7.5</span>
                            <span>= voto definitivo (partita finita da &gt;3h)</span>
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block min-w-[28px] px-1.5 py-0.5 rounded-full bg-yellow-400 text-black text-[9px] font-bold flex items-center justify-center">7.5</span>
                            <span>= voto provvisorio</span>
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block px-1.5 py-0.5 rounded-full bg-amber-500/90 text-white text-[9px] font-bold">Live - ...</span>
                            <span>= partita in corso, attesa dati</span>
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block px-1.5 py-0.5 rounded-full bg-emerald-600 text-white text-[9px] font-bold">Partita iniziata</span>
                            <span>= assenza dati ma partita già iniziata</span>
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[9px] font-bold">NP</span>
                            <span>= non sceso in campo (not played)</span>
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block px-1.5 py-0.5 rounded-full bg-gradient-to-r from-gray-500 to-gray-600 text-white text-[9px] font-bold">...</span>
                            <span>= voto in attesa di calcolo</span>
                          </span>
                        </div>
                      )}
                  </div>
                  {gameweekMatches?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-[rgba(0,217,255,0.2)]">
                      <div className="text-[#00D9FF] font-semibold text-xs mb-1">Partite della gameweek:</div>
                      <div className="flex flex-wrap gap-2">
                        {gameweekMatches.map((m, i) => (
                          <span key={m.matchId || i} className="text-white/90 text-xs voti-match-badge px-2 py-1">
                            {(m.homeTeam || '?')} vs {(m.awayTeam || '?')}{m.competitionName ? ` • ${m.competitionName}` : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {viewOnly && scoresLoaded && (
            <div className="mt-3 p-4 voti-gameweek-panel">
              <div className="flex items-start space-x-2">
                <span className="text-[#00D9FF] font-bold text-lg">ℹ️</span>
                <div className="flex-1">
                  <div className="font-bold text-white text-sm mb-1">Voti Gameweek</div>
                  <div className="text-gray-300 text-sm leading-relaxed">
                    {Object.keys(cardScores).length === 0 || Object.values(cardScores).every(score => score === null || score === undefined) 
                      ? (
                        tournamentScoresDisplayAsDefinitive(tournament) ? (
                          <span className="font-semibold text-white">
                            Gameweek conclusa: in assenza di voto salvato le card compaiono come NP.
                          </span>
                        ) : (
                          <span className="font-semibold text-white"> I voti non sono ancora disponibili per questa gameweek.</span>
                        )
                      )
                      : (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block min-w-[28px] px-1.5 py-0.5 rounded-full bg-emerald-600 text-white text-[9px] font-bold flex items-center justify-center">7.5</span>
                            <span>= voto definitivo (partita finita da &gt;3h)</span>
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block min-w-[28px] px-1.5 py-0.5 rounded-full bg-yellow-400 text-black text-[9px] font-bold flex items-center justify-center">7.5</span>
                            <span>= voto provvisorio</span>
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block px-1.5 py-0.5 rounded-full bg-amber-500/90 text-white text-[9px] font-bold">Live - ...</span>
                            <span>= partita in corso, attesa dati</span>
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block px-1.5 py-0.5 rounded-full bg-emerald-600 text-white text-[9px] font-bold">Partita iniziata</span>
                            <span>= assenza dati ma partita già iniziata</span>
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[9px] font-bold">NP</span>
                            <span>= non sceso in campo (not played)</span>
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block px-1.5 py-0.5 rounded-full bg-gradient-to-r from-gray-500 to-gray-600 text-white text-[9px] font-bold">...</span>
                            <span>= voto in attesa di calcolo</span>
                          </span>
                        </div>
                      )}
                  </div>
                  {gameweekMatches?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-[rgba(0,217,255,0.2)]">
                      <div className="text-[#00D9FF] font-semibold text-xs mb-1">Partite della gameweek:</div>
                      <div className="flex flex-wrap gap-2">
                        {gameweekMatches.map((m, i) => (
                          <span key={m.matchId || i} className="text-white/90 text-xs voti-match-badge px-2 py-1">
                            {(m.homeTeam || '?')} vs {(m.awayTeam || '?')}{m.competitionName ? ` • ${m.competitionName}` : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="formation-field-panel bg-white/80 backdrop-blur-xl p-6 mb-6 border border-[rgba(65,105,225,0.2)]">
            <FootballField 
              formation={formation}
              team={team}
              cardScores={cardScores}
              cardDetails={cardDetails}
              cardXp={cardXp}
              cardLiveNoData={cardLiveNoData}
              cardNotPlayed={cardNotPlayed}
              cardMatchDateTime={cardMatchDateTime}
              cardMatchInfo={cardMatchInfo}
              matchByPlayerId={matchByPlayerId}
              onPositionClick={viewOnly ? undefined : handlePositionClick}
              onPlayerRemove={viewOnly ? undefined : (card, role) => handleCardSelect(card, role)}
              allowLegends={toBoolFlag(tournament?.allow_legends ?? tournament?.allowLegends ?? tournament?.allow_legend ?? tournament?.allowLegend)}
              allowDivinities={toBoolFlag(tournament?.allow_divinities ?? tournament?.allowDivinities ?? tournament?.allow_divinity ?? tournament?.allowDivinity)}
              user={user}
              usedCardsInActiveGameweeks={usedCardsInOtherTournaments}
              scoresAreFinal={tournamentScoresDisplayAsDefinitive(tournament)}
            />
          </div>

          {!viewOnly && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-football w-full text-lg py-3 flex items-center justify-center space-x-2"
            >
              <Save className="w-5 h-5" />
              <span>{saving ? 'Saving...' : 'Save Formation'}</span>
            </button>
          )}
        </div>

        {/* Player Selection Modal - Portal to body; dark overlay; below navbar (120px mobile) */}
        {modalOpen && selectedPosition && createPortal(
          <div className="formation-player-modal fixed inset-0 z-[99999] bg-black/85 backdrop-blur-md flex items-start justify-center pt-[max(120px,env(safe-area-inset-top,0px)+80px)] md:pt-[56px] p-4 overflow-y-auto">
            <div className="football-card glass-football p-6 max-w-4xl w-full flex flex-col min-h-0 overflow-hidden flex-shrink-0 max-h-[calc(100vh-136px)] md:max-h-[calc(100vh-72px)]">
              <div className="flex-shrink-0 flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">
                  Select {selectedPosition.role === 'GK' ? 'Goalkeeper' : 
                          selectedPosition.role === 'DEF' ? 'Defender' : 
                          selectedPosition.role === 'MID' ? 'Midfielder' : 
                          selectedPosition.role === 'ATT' ? 'Attacker' :
                          selectedPosition.role === 'RES1' ? 'Reserve 1 (Defender)' :
                          selectedPosition.role === 'RES2' ? 'Reserve 2 (Midfielder)' :
                          selectedPosition.role === 'RES3' ? 'Reserve 3 (Attacker)' :
                          selectedPosition.role === 'LEGEND' ? 'Legend' :
                          'Divinity'}
                </h2>
                <div className="flex items-center gap-3">
                  {pendingLineSelection && (
                    <>
                      <span className="text-sm font-medium text-gray-700">
                        Selezionati: {pendingLineSelection.selected.length} / {pendingLineSelection.max}
                      </span>
                      <button
                        type="button"
                        onClick={handleConfirmLineSelection}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700"
                      >
                        Conferma
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setModalOpen(false);
                      setSelectedPosition(null);
                      setPendingLineSelection(null);
                    }}
                    className="modal-close-btn"
                    aria-label="Chiudi"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="flex-shrink-0 grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="md:col-span-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search player..."
                      value={filters.search}
                      onChange={(e) => {
                        setFilters({ ...filters, search: e.target.value });
                        loadUserCards(selectedPosition);
                      }}
                      className="pl-10 w-full"
                    />
                  </div>
                </div>
                <select
                  value={tournament?.rarity_restriction && tournament.rarity_restriction !== 'all' 
                    ? tournament.rarity_restriction 
                    : filters.rarity}
                  onChange={(e) => {
                    // Only allow changing if tournament allows all rarities
                    if (tournament?.rarity_restriction && tournament.rarity_restriction !== 'all') {
                      return; // Don't allow changing if tournament has specific rarity restriction
                    }
                    setFilters({ ...filters, rarity: e.target.value });
                    loadUserCards(selectedPosition);
                  }}
                  disabled={tournament?.rarity_restriction && tournament.rarity_restriction !== 'all'}
                  className=""
                  title={tournament?.rarity_restriction && tournament.rarity_restriction !== 'all' 
                    ? `This tournament only allows ${tournament.rarity_restriction} cards` 
                    : 'Filter by rarity'}
                >
                  <option value="">All Rarities</option>
                  <option value="basic">Basic</option>
                  <option value="plus">Plus</option>
                  <option value="rare">Rare</option>
                  <option value="epic">Epic</option>
                  <option value="unique">Unique</option>
                  <option value="legend">Legend</option>
                  <option value="divinity">Divinity</option>
                </select>
                <input
                  type="text"
                  placeholder="Nationality/Team"
                  value={filters.nationality}
                  onChange={(e) => {
                    setFilters({ ...filters, nationality: e.target.value });
                    loadUserCards(selectedPosition);
                  }}
                  className=""
                />
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto">
              {loading ? (
                <div className="text-center py-12">
                  <div className="football-spinner mx-auto"></div>
                  <p className="mt-4">Loading cards...</p>
                </div>
              ) : (() => {
                const isLegendSlot =
                  selectedPosition?.role === 'LEGEND' || selectedPosition?.role === 'DIVINITY';
                const visibleCards = filterFormationCardsWithoutNa(userCards, {
                  isLegendSlot,
                  teamCardIds: collectFormationTeamCardIds(team, pendingLineSelection),
                });
                if (visibleCards.length === 0) {
                  return (
                    <div className="text-center py-12">
                      {isWorldCupTournament(tournament) || userCards.length > 0 ? (
                        <p className="text-gray-300">
                          Nessuna carta con partita disponibile per questo ruolo in questa gameweek.
                        </p>
                      ) : (
                        <>
                          No {selectedPosition.role === 'GK' ? 'goalkeepers' :
                              selectedPosition.role === 'DEF' || selectedPosition.role === 'RES1' ? 'defenders' :
                              selectedPosition.role === 'MID' || selectedPosition.role === 'RES2' ? 'midfielders' :
                              selectedPosition.role === 'ATT' || selectedPosition.role === 'RES3' ? 'attackers' :
                              selectedPosition.role === 'LEGEND' ? 'legend cards' :
                              'divinity cards'} available. Purchase packs to get cards!
                        </>
                      )}
                    </div>
                  );
                }
                return (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {visibleCards.map((card) => {
                    const isInPendingLine = pendingLineSelection?.selected.some(c => c.id === card.id);
                    const isInCurrentTeam = pendingLineSelection
                      ? isInPendingLine
                      : (team.goalkeeper?.id === card.id ||
                        team.defenders.find(c => c.id === card.id) ||
                        team.midfielders.find(c => c.id === card.id) ||
                        team.attackers.find(c => c.id === card.id) ||
                        team.legend?.id === card.id ||
                        team.reserves?.RES1?.id === card.id ||
                        team.reserves?.RES2?.id === card.id ||
                        team.reserves?.RES3?.id === card.id);
                    const atLimitNoSelected = pendingLineSelection && !isInPendingLine && pendingLineSelection.selected.length >= pendingLineSelection.max;

                    const isUsedInOtherTournament = usedCardsInOtherTournaments.includes(card.id);
                    const formationCardsForDup = getFormationCardsForPlayerDupCheck(team, pendingLineSelection);
                    const dupPid = getCardPlayerId(card);
                    const isSamePlayerElsewhere =
                      dupPid != null &&
                      formationCardsForDup.some(
                        (k) => k && k.id !== card.id && getCardPlayerId(k) === dupPid
                      );
                    // Check if this card's next match is within range
                    const showsNotPlay = card.playsInRange === false;
                    const isOutOfDios10 =
                      card.player?.is_out_of_dios10 === true ||
                      card.player?.is_out_of_dios10 === 1 ||
                      card.player?.is_out_of_dios10 === '1' ||
                      card.player?.isOutOfDios10 === true ||
                      card.player?.isOutOfDios10 === 1 ||
                      card.player?.isOutOfDios10 === '1';
                    const isLegendSlot = selectedPosition?.role === 'LEGEND' || selectedPosition?.role === 'DIVINITY';
                    const formatUtc = (m) =>
                      m?.utcDate ? new Date(m.utcDate).toLocaleString() : null;
                    const match1Label = formatUpcomingMatchFirstLabel(card);
                    const match2Label = formatUtc(card.upcoming_match_second);
                    const showsNoMatch = !isLegendSlot && isMissingUpcomingMatchFirst(card);
                    
                    let currentPosition = null;
                    if (team.goalkeeper?.id === card.id) currentPosition = 'GK';
                    else if (team.defenders.find(c => c.id === card.id)) currentPosition = 'DEF';
                    else if (team.midfielders.find(c => c.id === card.id)) currentPosition = 'MID';
                    else if (team.attackers.find(c => c.id === card.id)) currentPosition = 'ATT';
                    else if (team.reserves?.RES1?.id === card.id) currentPosition = 'RES1';
                    else if (team.reserves?.RES2?.id === card.id) currentPosition = 'RES2';
                    else if (team.reserves?.RES3?.id === card.id) currentPosition = 'RES3';
                    else if (team.legend?.id === card.id) {
                      const k = String(card.card_kind || card.cardKind || '').toLowerCase();
                      const r = String(card.rarity?.name || '').toLowerCase();
                      const isDivinity = k === 'divinity' || r === 'divinity' || !!card.player?.is_divinity;
                      currentPosition = isDivinity ? 'DIVINITY' : 'LEGEND';
                    }
                    
                    // In multi-select: disable if used elsewhere, at limit (and not already selected), not playing, or OUT OF DIOS.
                    // Otherwise: used elsewhere, in team but not this slot, not playing, or OUT OF DIOS.
                    const isDisabled = isUsedInOtherTournament || showsNotPlay || showsNoMatch || isOutOfDios10 || isSamePlayerElsewhere ||
                      (pendingLineSelection ? atLimitNoSelected : (isInCurrentTeam && !currentPosition));
                    
                    return (
                      <button
                        key={card.id}
                        onClick={() => {
                          // Extra safety check - don't allow selection if disabled, used elsewhere, not playing or OUT OF DIOS
                          if (isDisabled || isUsedInOtherTournament || showsNotPlay || showsNoMatch || isOutOfDios10 || isSamePlayerElsewhere) {
                            return;
                          }
                          handleCardSelectFromModal(card);
                        }}
                        disabled={isDisabled}
                        className={`football-card player-pick-card p-4 text-center transition-all relative ${
                          isDisabled 
                            ? 'opacity-50 cursor-not-allowed' 
                            : 'hover:scale-105 cursor-pointer'
                        } ${
                          isInCurrentTeam 
                            ? 'ring-2 ring-yellow-400 bg-yellow-400/20' 
                            : ''
                        }`}
                        title={
                          isOutOfDios10
                            ? 'This player is OUT OF DIOS and cannot be selected'
                            : showsNoMatch
                            ? 'Nessuna partita disponibile per questo giocatore'
                            : showsNotPlay
                            ? 'This player\'s team does not play during this tournament' 
                            : isSamePlayerElsewhere
                              ? 'Stesso giocatore già presente in formazione (altra carta)'
                            : isDisabled 
                              ? 'This player is already used in another tournament of this gameweek' 
                              : isInCurrentTeam 
                                ? `Already selected as ${currentPosition}` 
                                : 'Click to select'
                        }
                      >
                        {isInCurrentTeam && (
                          <div className="absolute top-2 right-2 bg-yellow-400 text-black text-xs font-bold px-2 py-1 z-10">
                            {currentPosition}
                          </div>
                        )}
                        {isUsedInOtherTournament && (
                          <div className="absolute top-2 left-2 bg-red-100 border border-red-300 text-red-800 text-xs font-bold px-2 py-1 z-10">
                            Used
                          </div>
                        )}
                        {showsNotPlay && (
                          <div className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded z-10 text-shadow-sm">
                            NOT PLAY
                          </div>
                        )}
                        {showsNoMatch && (
                          <div className="absolute top-2 left-2 bg-gray-700 text-white text-xs font-bold px-2 py-1 rounded z-10 text-shadow-sm">
                            N/A
                          </div>
                        )}
                        {isOutOfDios10 && (
                          <div className="absolute top-10 left-2 bg-red-900/95 border border-red-600 text-red-100 text-xs font-bold px-2 py-1 rounded z-10 text-shadow-sm">
                            OUT OF DIOS
                          </div>
                        )}
                        {isSamePlayerElsewhere && (
                          <div className="absolute top-10 right-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded z-10 text-shadow-sm">
                            Duplicate
                          </div>
                        )}
                        <div className={`w-full h-32 bg-gradient-to-br ${
                          isDisabled 
                            ? 'from-gray-500 to-gray-700' 
                            : 'from-blue-400 to-cyan-500'
                        } flex flex-col items-center justify-center mb-2`}>
                          <div className="text-white text-sm font-bold text-center whitespace-normal leading-tight px-1">
                            {(() => {
                              const cardKind = String(card.card_kind || card.cardKind || '').toLowerCase();
                              const rarityName = String(card.rarity?.name || '').toLowerCase();
                              const isSpecialCard =
                                cardKind === 'legend' ||
                                cardKind === 'divinity' ||
                                rarityName === 'legend' ||
                                rarityName === 'divinity' ||
                                !!card.player?.is_legend ||
                                !!card.player?.is_divinity;
                              if (isSpecialCard) {
                                return (
                                  card.custom_name ||
                                  card.customName ||
                                  card.player?.display_name ||
                                  card.player?.full_name ||
                                  card.player?.last_name ||
                                  'Player'
                                );
                              }
                              return card.player?.display_name || card.player?.last_name || card.player?.full_name?.split(' ').slice(-1)[0] || 'Player';
                            })()}
                          </div>
                          {card.player?.team && (
                            <div className="text-white/90 text-xs mt-1 text-center">
                              {card.player.team}
                            </div>
                          )}
                        </div>
                        <div className="player-pick-card-meta mt-1.5 text-sm sm:text-base font-bold text-center leading-snug text-white">
                          {card.rarity?.display_name || 'Basic'} · {card.player?.position || 'N/A'}
                          {(card.xp_percentage != null || card.xpPercentage != null) && (
                            <>
                              {' '}
                              · XP&nbsp;{Number(card.xp_percentage ?? card.xpPercentage ?? 0).toFixed(1)}%
                            </>
                          )}
                        </div>
                        <div className="mt-1 space-y-0.5 text-xs text-center leading-snug text-gray-300">
                          <div>{match1Label ? `Match 1: ${match1Label}` : 'Match 1: N/A'}</div>
                          <div>{match2Label ? `Match 2: ${match2Label}` : 'Match 2: —'}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                );
              })()}
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default TournamentFormation;

